import { notification } from "antd";
import api from "./api-base";
import { useMutation, useQuery, useQueryClient } from "react-query";

//Auth API
const validateLogin = async (payload) => await api.post(`/authenticate`, payload);

//Auth Queries
export function useValidateLogin() {
    return useMutation(validateLogin);
}

//Product API
const getProducts = async () => await api.get(`/product`);

const getProductById = async (productId) => await api.get(`/product/${productId}`);

const addProduct = async (payload) => await api.post(`/product`, payload);

const editProduct = async ({ productId, payload }) => await api.put(`/product/${productId}`, payload);

const deleteProduct = async (productId) => await api.delete(`/product/${productId}`);
//Product Queries
export function useGetProducts() {
    return useQuery(['getProducts'], getProducts);
}

export function useGetProductById(productId) {
    return useQuery(['getProductById', productId],
        () => getProductById(productId), 
        { enabled: !!productId, cacheTime: 0 });
}

export function useAddProduct() {
    const queryClient = useQueryClient();

    return useMutation(addProduct, {
        onSuccess: () => {
            notification.success({ message: "Product saved successfully" });
            queryClient.invalidateQueries('getProducts');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to save product' })
    });
}

export function useEditProduct() {
    const queryClient = useQueryClient();

    return useMutation(editProduct, {
        onSuccess: () => {
            notification.success({ message: "Product updated successfully" });
            queryClient.invalidateQueries('getProducts');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to update product' })
    });
}

//Company API
const getCompany = async () => await api.get(`/company`);

const getCompanyById = async (companyId) => await api.get(`/company/${companyId}`);
const addCompany = async (payload) => await api.post(`/company`, payload);

const editCompany = async ({ companyId, payload }) => await api.put(`/company/${companyId}`, payload);



export function useGetCompany() {
    return useQuery(['getCampany'], getCompany);
}

export function useAddCompany() {
    const queryClient = useQueryClient();
    return useMutation(addCompany, {
        onSuccess: () => {
            notification.success({ message: "Company saved successfully" });
            queryClient.invalidateQueries('getCompany');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to save company' })
    });
}

export function useGetCompanyById(companyId) {
    return useQuery(['getCompanyById'],
        () => getCompanyById(companyId), { enabled: !!companyId });
}

export function useEditCompany() {
    const queryClient = useQueryClient();
    return useMutation(editCompany, {
        onSuccess: () => {
            notification.success({ message: "Company updated successfully" });
            queryClient.invalidateQueries('getCompany');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to update company' })
    }
    );
}


//Role API
const getRole = async () => await api.get(`/roles`);

const getRoleById = async (roleId) => await api.get(`/roles/${roleId}`);

const addRole = async (payload) => await api.post(`/roles`, payload);

const editRole = async ({ roleId, payload }) => await api.put(`/roles/${roleId}`, payload);



export function useGetRole() {
    return useQuery(['getRole'], getRole);
}

export function useAddRole() {
    const queryClient = useQueryClient();
    return useMutation(addRole, {
        onSuccess: () => {
            notification.success({ message: "Role saved successfully" });
            queryClient.invalidateQueries('getRole');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to save role' })
    });
}

export function useGetRoleById(roleId) {
    return useQuery(['getRoleById'],
        () => getRoleById(roleId), { enabled: !!roleId });
}

export function useEditRole() {
    const queryClient = useQueryClient();
    return useMutation(editRole, {
        onSuccess: () => {
            notification.success({ message: "Role updated successfully" });
            queryClient.invalidateQueries('getRole');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to update role' })
    }
    );
}


//Users API
const getUser = async () => await api.get(`/user`);

const getUserById = async (userId) => await api.get(`/user/${userId}`);

const getUserByRoleId = async (roleId) => await api.get(`/user/role/${roleId}`);

const addUser = async (payload) => await api.post(`/user`, payload);

const editUser = async ({ userId, payload }) => await api.put(`/user/${userId}`, payload);

const resetPassword = async (payload) => await api.post(`/user/resetpassword`, payload);

const changePassword = async (payload) => await api.post(`/user/changepassword`, payload);

const deleteUser = async (userId) => await api.delete(`/user/${userId}`);


export function useDeleteUser() {
    const queryClient = useQueryClient();

    return useMutation(deleteUser, {
        onSuccess: () => {
            notification.success({ message: "User deleted successfully" });
            queryClient.invalidateQueries('getUsers');
            queryClient.invalidateQueries('getUserByRoleId');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to delete user' })
    });
}

export function useGetUser() {
    return useQuery(['getUser'], getUser);
}

export function useAddUser() {
    const queryClient = useQueryClient();
    return useMutation(addUser, {
        onSuccess: () => {
            notification.success({ message: "User saved successfully" });
            queryClient.invalidateQueries('getUsers');
            queryClient.invalidateQueries('getUserByRoleId');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to save user' })
    });
}

export function useGetUserById(userId) {
    return useQuery(['getUserById'],
        () => getUserById(userId), { enabled: !!userId });
}

export function useGetUserByRoleId(roleId) {
    return useQuery(['getUserByRoleId', roleId],
        () => getUserByRoleId(roleId), 
        { 
            enabled: !!roleId, 
            cacheTime: 0 
        });
}

export function useEditUser() {
    const queryClient = useQueryClient();
    return useMutation(editUser, {
        onSuccess: () => {
            notification.success({ message: "User updated successfully" });
            queryClient.invalidateQueries('getUsers');
            queryClient.invalidateQueries('getUserByRoleId');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to update user' })
    }
    );
}

export function useResetPassword() {
    const queryClient = useQueryClient();

    return useMutation(resetPassword, {
        onSuccess: () => {
            notification.success({ message: "User password reset was successful..." });
            queryClient.invalidateQueries('getUsers');
            queryClient.invalidateQueries('getUserByRoleId');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to reset password' })
    });
}

export function useChangePassword() {
    const queryClient = useQueryClient();

    return useMutation(changePassword, {
        onSuccess: () => {
            notification.success({ message: "User password changed successfully" });
            queryClient.invalidateQueries('getUsers');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to change password' })
    });
}

// Product Default Values API
const getDefaultCost = async (productId) => await api.get(`/defaultCost/product/${productId}`);

const addBulkDefaultCost = async (payload) => await api.post(`/defaultCost/bulk`, payload);

export function useGetDefaultCost(productId) {
    return useQuery(['getDefaultCost'],
        () => getDefaultCost(productId), { enabled: !!productId });
}

export function useAddBulkDefaultCost() {
    const queryClient = useQueryClient();

    return useMutation(addBulkDefaultCost, {
        onSuccess: () => {
            notification.success({ message: "Default costs successfully" });
            queryClient.invalidateQueries('getDefaultCost');
            queryClient.invalidateQueries('getProducts');
        }
    });
}

const getHoliday = async (holidayDate) => await api.get(`/holiday/${holidayDate}`);

export function useGetHoliday(holidayDate) {
    return useQuery(['getHoliday'], () => getHoliday(holidayDate), 
        { enabled: !!holidayDate });
}

const addHoliday = async (payload) => await api.post(`/holiday`, payload);

export function useAddHoliday() {
    const queryClient = useQueryClient();
    return useMutation(addHoliday, {
        onSuccess: () => {
            notification.success({ message: "Holiday saved successfully" });
            queryClient.invalidateQueries('getHoliday');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to save holiday' })
    });
}

const addBulkHoliday = async (payload) => await api.post(`/holiday/bulk`, payload);

export function useAddBulkHoliday() {
    const queryClient = useQueryClient();
    return useMutation(addBulkHoliday, {
        onSuccess: () => {
            notification.success({ message: "Holidays saved successfully" });
            queryClient.invalidateQueries('getHoliday');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to save holidays' })
    });
}

const editHoliday = async ({ holidayId, payload }) => await api.put(`/holiday/${holidayId}`, payload);

export function useEditHoliday() {
    const queryClient = useQueryClient();
    return useMutation(editHoliday, {
        onSuccess: () => {
            notification.success({ message: "Holiday updated successfully" });
            queryClient.invalidateQueries('getHoliday');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to update holiday' })
    });
}

const checkHoliday = async (holidayDate) => await api.get(`/holiday/${holidayDate }`);

export function useCheckHoliday(holidayDate) {
    return useQuery(['checkHoliday', holidayDate], 
        () => checkHoliday(holidayDate), 
        { enabled: !!holidayDate });
}

const deleteHoliday = async (holidayId) => await api.delete(`/holiday/${holidayId}`);

export function useDeleteHoliday() {
    const queryClient = useQueryClient();
    return useMutation(deleteHoliday, {
        onSuccess: () => {
            notification.success({ message: "Holiday deleted successfully" });
            queryClient.invalidateQueries('getHoliday');
        },
        onError: (err) => notification.error({ message: err.message || 'Failed to delete holiday' })
    });
}
